import { isNotEmpty, type DashboardData, type DashboardWidget } from '@terros/common'
import { sortDashboardsByOrder } from './order'
import { NUMBER_WIDGET_FULL_ROW_METRIC_THRESHOLD } from './metricGrid'

/**
 * One dashboard's contribution to the Home feed: the dashboard itself and the
 * widgets of it that can render, in their configured order. Rendered as a
 * single feed section titled with the dashboard's name.
 */
export type DashboardFeedGroup<T extends DashboardData = DashboardData> = {
  dashboard: T
  widgets: DashboardWidget[]
}

/**
 * Widgets in a dashboard that can render in the feed. KPI-backed widgets need
 * at least one configured metric; goal widgets use the goals data path and
 * have no metrics by design.
 */
export function getVisibleDashboardWidgets(dashboard: DashboardData): DashboardWidget[] {
  return dashboard.widgets.filter((widget) => widget.type === 'goal' || isNotEmpty(widget.metrics))
}

/**
 * Whether the widget spans a full feed row. Line and goal widgets always do,
 * as does a number widget with more than 3 metrics — multiple KPIs need the
 * horizontal space. Table and leaderboard widgets share a row.
 */
export function isFullRowFeedWidget(widget: DashboardWidget): boolean {
  switch (widget.type) {
    case 'goal':
    case 'line':
      return true
    case 'number':
      return widget.metrics.length >= NUMBER_WIDGET_FULL_ROW_METRIC_THRESHOLD
    case 'leaderboard':
    case 'table':
      return false
  }
}

/**
 * The viewer's non-hustle dashboards that contribute at least one renderable
 * widget, in the viewer's dashboard order — each renders as its own feed
 * section under the dashboard's name. Dashboards with nothing to render are
 * dropped so they don't leave an empty header behind.
 *
 * The hustle dashboard is pinned separately at the top of the feed, so it is
 * excluded here to avoid duplication.
 */
export function getDashboardFeedGroups<T extends DashboardData>(dashboards: T[]): DashboardFeedGroup<T>[] {
  return [...dashboards]
    .filter((dashboard) => dashboard.home !== true)
    .sort(sortDashboardsByOrder)
    .map((dashboard) => ({ dashboard, widgets: getVisibleDashboardWidgets(dashboard) }))
    .filter((group) => isNotEmpty(group.widgets))
}
